import { defineStore } from 'pinia';
import { useProfileStore } from '@/store/profileStore';
import { getTenant } from '@/utils/helpers/urlManipulation';

/**
 * Feature flag definitions
 *
 * Feature flags allow tenant-specific features to be enabled or disabled via environment variables.
 * Each flag corresponds to a feature that can be controlled independently per tenant.
 */
export interface FeatureFlags {
  /**
   * Document Paths Feature
   *
   * When enabled, this feature allows:
   * - Projects: Specifying `project_documents_path` (absolute HTTPS URL to document root)
   * - Company Documents: Specifying `relative_path` (relative path from document root)
   * - Template Documents: Specifying `relative_path` (relative path from document root)
   *
   * Environment variable: VITE_{tenant}_Feature_Flag_DocumentPaths
   * Example: VITE_beeboard_Feature_Flag_DocumentPaths=1
   */
  documentPaths: boolean;

  /**
   * Mirror Feature
   *
   * When enabled it is possible to mirror between projects.
   *
   * Environment variable: VITE_{tenant}_Feature_Flag_Mirror
   * Example: VITE_beeboard_Feature_Flag_Mirror=1
   */
  mirror: boolean;

  /**
   * Traffic Light Feature
   *
   * When enabled, activities can have colored status indicators (red/yellow/green).
   *
   * Environment variable: VITE_{tenant}_traffic_light
   * Example: VITE_instone_traffic_light=1
   */
  trafficLight: boolean;

  /**
   * AI Feature
   *
   * When enabled, AI-powered features are available.
   *
   * Environment variable: VITE_{tenant}_AI
   * Example: VITE_beeboard_AI=1
   */
  ai: boolean;

  /**
   * Meeting Transcripts Feature
   *
   * When enabled, users can access Microsoft Teams meeting transcripts with AI summaries.
   * Requires both AI feature flag and Microsoft Todo integration to be enabled.
   *
   * Requirements:
   * - VITE_{tenant}_AI=1
   * - VITE_{tenant}_MSTODO_TENANT_ID configured
   * - VITE_{tenant}_MSTODO_CLIENT_ID configured
   *
   * Optional Access Control:
   * - VITE_{tenant}_MEETING_TRANSCRIPTS_ALLOWED_EMAILS: Comma-separated list of email addresses
   *   that are allowed to access meeting transcripts. If not set, all users can access the feature.
   *   Example: VITE_beeboard_MEETING_TRANSCRIPTS_ALLOWED_EMAILS="user1@company.com,user2@company.com"
   */
  meetingTranscripts: boolean;

  /**
   * URL Manipulation (WinEx)
   *
   * When enabled, allows custom URL manipulation rules for document links.
   *
   * Environment variable: VITE_{tenant}_winex
   * Example: VITE_dreso_winex=1
   */
  urlManipulation: boolean;

  /**
   * Predecessor State Column
   *
   * When enabled, shows predecessor state information in activity views.
   *
   * Environment variable: VITE_{tenant}_predecessorstate_column
   * Example: VITE_beeboard_predecessorstate_column=1
   */
  predecessorStateColumn: boolean;

  /**
   * Easter Egg Feature
   *
   * When active (current date falls within the hardcoded time range),
   * a random activity on the map shows bunny ears. Clicking the ears
   * transforms all hexagons into sprinkled Easter eggs.
   */
  easterEgg: boolean;

  /**
   * Views Feature
   *
   * When enabled, users can access the custom views feature for cross-project
   * activity and milestone visualization.
   *
   * Environment variable: VITE_{tenant}_VIEWS
   * Example: VITE_beeboard_VIEWS=1
   *
   * Optional Access Control:
   * - VITE_{tenant}_VIEWS_ALLOWED_EMAILS: Comma-separated list of email addresses
   *   that are allowed to access views. If not set, all users can access the feature.
   *   Example: VITE_beeboard_VIEWS_ALLOWED_EMAILS="user1@company.com,user2@company.com"
   */
  views: boolean;

  /**
   * Meeting Summary Batch Processing Feature
   *
   * When enabled, users can select multiple meetings and process them in batch
   * to generate AI summaries. Requires Meeting Transcripts feature to be enabled.
   *
   * Environment variable: VITE_{tenant}_MEETING_BATCH_PROCESSING
   * Example: VITE_beeboard_MEETING_BATCH_PROCESSING=1
   */
  meetingBatchProcessing: boolean;
}

/**
 * Feature Flag Store
 *
 * Centralized store for managing tenant-specific feature flags.
 * Reads feature flag values from environment variables based on the current tenant.
 *
 * @example
 * ```ts
 * const featureFlagStore = useFeatureFlagStore();
 * if (featureFlagStore.hasDocumentPaths) {
 *   // Show document path fields
 * }
 * ```
 */
export const useFeatureFlagStore = defineStore('featureFlagStore', {
  getters: {
    /**
     * Get the current tenant identifier from the hostname
     */
    tenant(): string {
      return getTenant();
    },

    /**
     * Check if the Document Paths feature is enabled for the current tenant
     *
     * When enabled, document forms and project forms will show.
     */
    hasDocumentPaths(): boolean {
      const envValue = import.meta.env[
        `VITE_${this.tenant}_Feature_Flag_DocumentPaths`
      ];

      return envValue === '1';
    },

    /**
     * Check if the Mirror (parent projects) feature is enabled for the current tenant
     */
    hasMirror(): boolean {
      const envValue = import.meta.env[
        `VITE_${this.tenant}_Feature_Flag_Mirror`
      ];

      return envValue === '1';
    },

    /**
     * Check if the Traffic Light feature is enabled for the current tenant
     */
    hasTrafficLight(): boolean {
      const envValue = import.meta.env[`VITE_${this.tenant}_traffic_light`];

      return envValue === '1';
    },

    /**
     * Check if AI features are enabled for the current tenant
     */
    hasAI(): boolean {
      const envValue = import.meta.env[`VITE_${this.tenant}_AI`];

      return envValue === '1';
    },

    /**
     * Check if URL manipulation (WinEx) is enabled for the current tenant
     */
    hasUrlManipulation(): boolean {
      const envValue = import.meta.env[`VITE_${this.tenant}_winex`];

      return envValue === '1';
    },

    /**
     * Check if predecessor state column is enabled for the current tenant
     */
    hasPredecessorStateColumn(): boolean {
      const envValue = import.meta.env[
        `VITE_${this.tenant}_predecessorstate_column`
      ];

      return envValue === '1';
    },

    /**
     * Check if Meeting Transcripts feature is enabled for the current tenant
     *
     * Requires both:
     * 1. AI feature flag enabled
     * 2. Microsoft Todo integration configured
     */
    hasMeetingTranscripts(): boolean {
      // Check AI feature flag
      if (!this.hasAI) {
        return false;
      }

      // Check if Microsoft Todo is configured
      const todoTenantId = import.meta.env[
        `VITE_${this.tenant}_MSTODO_TENANT_ID`
      ];
      const todoClientId = import.meta.env[
        `VITE_${this.tenant}_MSTODO_CLIENT_ID`
      ];

      return todoTenantId !== undefined && todoClientId !== undefined;
    },

    /**
     * Check if Meeting Transcripts are accessible for the *current user*.
     *
     * `hasMeetingTranscripts` answers "is the feature configured for this
     * tenant"; this getter additionally applies the optional per-user
     * allowlist `VITE_{tenant}_MEETING_TRANSCRIPTS_ALLOWED_EMAILS`. If no
     * allowlist is configured, every user of the tenant has access
     * (backward compatible).
     *
     * Anything that exposes meeting content — the nav entry, the routes, and
     * the AI assistant's meeting tools — must gate on this getter, not on
     * `hasMeetingTranscripts`: the assistant's meeting tools have no backend
     * gate, so a tenant-level-only check would hand transcript content to
     * users the allowlist was meant to exclude.
     *
     * Mirrors the shape of `hasViews` above.
     */
    canAccessMeetingTranscripts(): boolean {
      if (!this.hasMeetingTranscripts) {
        return false;
      }

      const allowedEmailsEnv = import.meta.env[
        `VITE_${this.tenant}_MEETING_TRANSCRIPTS_ALLOWED_EMAILS`
      ];

      if (!allowedEmailsEnv) {
        return true;
      }

      const allowedEmails = allowedEmailsEnv
        .split(',')
        .map((email: string) => email.trim().toLowerCase());

      const userEmail = useProfileStore().profile.email?.toLowerCase();

      return !!userEmail && allowedEmails.includes(userEmail);
    },

    /**
     * Check if the Easter Egg feature is currently active
     *
     * Returns true only when the current date falls within the
     * hardcoded Easter time range (April 14 – April 22, 2026).
     */
    hasEasterEgg(): boolean {
      const now = new Date();
      const start = new Date('2026-03-30');
      const end = new Date('2026-04-06');

      return now >= start && now <= end;
    },

    /**
     * Check if Views feature is accessible for the current user
     *
     * Requires VITE_{tenant}_VIEWS=1 to enable the feature for a tenant.
     * If VITE_{tenant}_VIEWS_ALLOWED_EMAILS is additionally set, only listed emails can access.
     * If not set, all users of that tenant can access views.
     */
    hasViews(): boolean {
      const envValue = import.meta.env[`VITE_${this.tenant}_VIEWS`];
      if (envValue !== '1') {
        return false;
      }

      const allowedEmailsEnv = import.meta.env[
        `VITE_${this.tenant}_VIEWS_ALLOWED_EMAILS`
      ];

      if (!allowedEmailsEnv) {
        return true;
      }

      const allowedEmails = allowedEmailsEnv
        .split(',')
        .map((email: string) => email.trim().toLowerCase());

      const userEmail = useProfileStore().profile.email?.toLowerCase();

      return !!userEmail && allowedEmails.includes(userEmail);
    },

    /**
     * Check if Meeting Batch Processing feature is enabled for the current tenant
     *
     * Requires Meeting Transcripts feature to be enabled.
     */
    hasMeetingBatchProcessing(): boolean {
      // Requires meeting transcripts to be enabled
      if (!this.hasMeetingTranscripts) {
        return false;
      }

      const envValue = import.meta.env[
        `VITE_${this.tenant}_MEETING_BATCH_PROCESSING`
      ];

      return envValue === '1';
    },

    /**
     * Activity layout engine for the canvas Map renderer (WI #5531).
     *
     * 'flow' (default) is the zoom-stable layout: vertical order is fixed by
     * a full-zoom-out packing, local groups of crowding activities spread
     * across the band (banner-friendly spacing), and zooming blends the
     * layout continuously — no lane reshuffling. 'classic' is the legacy
     * store lane packing (screen-space first-fit), kept as an instant opt-out
     * for A/B comparison via localStorage `canvasActivityLayout=classic` or
     * `?canvasActivityLayout=classic`.
     */
    canvasActivityLayout(): 'flow' | 'classic' {
      if (typeof window !== 'undefined') {
        try {
          const stored = window.localStorage?.getItem('canvasActivityLayout');
          if (stored === 'flow' || stored === 'classic') {
            return stored;
          }
          const param = new URLSearchParams(window.location.search).get(
            'canvasActivityLayout',
          );
          if (param === 'flow' || param === 'classic') {
            return param;
          }
        } catch {
          // Ignore storage/URL access errors and use the default.
        }
      }

      return 'flow';
    },

    /**
     * Get all feature flags as a single object
     *
     * @returns Object with all feature flag values
     */
    allFlags(): FeatureFlags {
      return {
        documentPaths: this.hasDocumentPaths,
        mirror: this.hasMirror,
        trafficLight: this.hasTrafficLight,
        ai: this.hasAI,
        meetingTranscripts: this.hasMeetingTranscripts,
        urlManipulation: this.hasUrlManipulation,
        predecessorStateColumn: this.hasPredecessorStateColumn,
        views: this.hasViews,
        meetingBatchProcessing: this.hasMeetingBatchProcessing,
        easterEgg: this.hasEasterEgg,
      };
    },
  },
});
